import { parseISO } from 'date-fns';
import { compact, maxBy } from 'lodash';
import { BallType, FIVE_OZ_BALL_TYPES } from '../enums/machine.enums';
import {
  GroupCriteria,
  GroupRejectedReason,
  MAX_BREAK_THRESHOLD_FT,
  MIN_BREAK_CONFIDENCE,
  MIN_BREAK_THRESHOLD_FT,
  ShotCriteria,
  getConfidenceCriteria,
} from '../enums/shot-rejection.enums';
import { TrackingDevice } from '../enums/training.enums';
import { IPitch } from '../interfaces/pitches';
import {
  ICollectionShotGroup,
  IMachineShot,
  IRawCollectionShotGroup,
} from '../interfaces/training/i-machine-shot';
import { IPresetTrainingSpec } from '../interfaces/training/i-preset-training-spec';
import { ArrayHelper } from './array.helper';
import { BallHelper } from './ball.helper';
import { MachineHelper } from './machine.helper';
import { METERS_TO_FT, meanObject } from './math.utilities';
import { PlateHelper, VISIBLE_STRIKEZONE } from './plate.helper';
import { TrajHelper } from './trajectory.helper';

/** only auto-adjust shots that land within the strike zone + a certain buffer around */
const SZ_GREEN_BUFFER_FT = 7 / 12;

export class ShotRejectionHelper {
  static filterShot(config: { shot: IMachineShot; criteria: ShotCriteria[] }): {
    reject: boolean;
    reasons: ShotCriteria[];
  } {
    const { shot, criteria } = config;

    const {
      confidence: conf,
      break: brks, // because "break" is a reserved keyword
      bs,
      target_ms,
      ball_type,
    } = shot;

    const reasons: ShotCriteria[] = [];

    for (const crit of criteria) {
      switch (crit) {
        case ShotCriteria.ExistsBallState: {
          if (
            // * tracking_device should always be defined
            shot.tracking_device &&
            // * these devices would not be expected to come w/ bs values
            [TrackingDevice.TrackmanB1, TrackingDevice.TrajektVision].includes(
              shot.tracking_device
            )
          ) {
            break;
          }

          if (bs) {
            break;
          }

          // e.g. rapsodo and hawkeye as of 2025-09
          reasons.push(crit);
          break;
        }

        case ShotCriteria.ConfidenceSpin: {
          if (
            // * tracking_device should always be defined
            shot.tracking_device &&
            // * these devices would not be expected to come w/ bs values
            [TrackingDevice.TrackmanB1, TrackingDevice.TrajektVision].includes(
              shot.tracking_device
            )
          ) {
            break;
          }

          if (conf?.PITCH_SpinConfidence === 1) {
            break;
          }

          // e.g. rapsodo and hawkeye as of 2025-09
          reasons.push(crit);
          break;
        }

        case ShotCriteria.ConfidenceBreak: {
          if (!brks) {
            // no break confidence object found
            reasons.push(crit);
            break;
          }

          const {
            PITCH_HBTrajectory,
            PITCH_HBTrajectoryConfidence,
            PITCH_VBTrajectory,
            PITCH_VBTrajectoryConfidence,
          } = brks;

          // * rapsodo ALWAYS assigns LC to anything w/ breaks over 2.25ft
          // this check only rejects shots for LC for breaks outside 2.25-3'
          // anything within the limits, we count as not LC (ignoring rapsodo's LC )

          if (PITCH_VBTrajectoryConfidence !== MIN_BREAK_CONFIDENCE) {
            const absFt = Math.abs(PITCH_VBTrajectory) * METERS_TO_FT;

            if (
              absFt < MIN_BREAK_THRESHOLD_FT ||
              absFt > MAX_BREAK_THRESHOLD_FT
            ) {
              reasons.push(crit);
              break;
            }
          }

          if (PITCH_HBTrajectoryConfidence !== MIN_BREAK_CONFIDENCE) {
            const absFt = Math.abs(PITCH_HBTrajectory) * METERS_TO_FT;

            if (
              absFt < MIN_BREAK_THRESHOLD_FT ||
              absFt > MAX_BREAK_THRESHOLD_FT
            ) {
              reasons.push(crit);
              break;
            }
          }
          break;
        }

        case ShotCriteria.ConfidencePlates: {
          if (
            conf?.PITCH_StrikePositionSideConfidence !== 1 ||
            conf?.PITCH_StrikePositionHeightConfidence !== 1
          ) {
            reasons.push(crit);
          }
          break;
        }

        case ShotCriteria.ConfidenceSpeed: {
          if (conf?.PITCH_SpeedConfidence !== 1) {
            reasons.push(crit);
          }
          break;
        }

        case ShotCriteria.ModelGyroSpin: {
          if (!bs) {
            // problem is w/ lack of bs, not necessarily w/ the gyro spin
            break;
          }

          // e.g. from rapsodo or hawkeye
          if (!BallHelper.has3DSpin(bs)) {
            reasons.push(crit);
            break;
          }

          const pSpin = MachineHelper.predictSpin(target_ms);
          if (Math.abs(bs.wy - pSpin.wy) <= 600) {
            break;
          }

          // i.e. flag when tracking device reports extremely different gyro spin
          reasons.push(crit);
          break;
        }

        case ShotCriteria.ModelNonGyroSpin: {
          if (!bs) {
            // problem is w/ lack of bs, not necessarily w/ the non-gyro spins
            break;
          }

          // e.g. from rapsodo or hawkeye
          if (!BallHelper.has3DSpin(bs)) {
            reasons.push(crit);
            break;
          }

          const pSpin = MachineHelper.predictSpin(target_ms);

          const maxAbsDiff = Math.max(
            Math.abs(bs.wx - pSpin.wx),
            Math.abs(bs.wz - pSpin.wz)
          );

          if (maxAbsDiff <= 1500) {
            break;
          }

          // i.e. flag when tracking device reports extremely different spins
          reasons.push(crit);
          break;
        }

        case ShotCriteria.ModelZeroTrajBreak: {
          if (!brks) {
            reasons.push(crit);
            break;
          }

          const minAbsValue = Math.min(
            Math.abs(brks.PITCH_HBTrajectory),
            Math.abs(brks.PITCH_VBTrajectory)
          );

          if (minAbsValue < 1e-6) {
            reasons.push(crit);
          }
          break;
        }

        case ShotCriteria.RapsodoBreakAgreement: {
          // * only applies to Rapsodo detections
          if (shot.tracking_device !== TrackingDevice.RapsodoV3PRO) {
            break;
          }

          // * only applies to 5oz ball types
          if (!FIVE_OZ_BALL_TYPES.includes(ball_type as BallType)) {
            break;
          }

          if (!brks) {
            reasons.push(crit);
            break;
          }

          const maxAbsDiff = Math.max(
            Math.abs(brks.PITCH_HBSpin - brks.PITCH_HBTrajectory),
            Math.abs(brks.PITCH_VBSpin - brks.PITCH_VBTrajectory)
          );

          if (maxAbsDiff * METERS_TO_FT > 1.5) {
            reasons.push(crit);
          }
          break;
        }

        default: {
          break;
        }
      }
    }

    return {
      reject: reasons.length > 0,
      reasons: reasons,
    };
  }

  // handles all of what was formerly "isStrictlyAcceptable"
  static filterGroup(config: {
    target: IPitch;
    shots: IMachineShot[];
    spec: IPresetTrainingSpec;
    trainingThreshold: number;
    criteria: GroupCriteria[];
  }): {
    reject: boolean;
    reasons: GroupRejectedReason[];
  } {
    const { shots, spec, target, criteria, trainingThreshold } = config;

    const reasons: GroupRejectedReason[] = [];

    if (shots.length < trainingThreshold) {
      return {
        reject: true,
        reasons: [GroupRejectedReason.TooFew],
      };
    }

    for (const crit of criteria) {
      switch (crit) {
        case GroupCriteria.Speed: {
          const trajs = compact(shots.map((s) => s.traj));
          const meanTraj = meanObject(trajs);

          if (!meanTraj) {
            reasons.push(GroupRejectedReason.NoMeanTraj);
            break;
          }

          const targetMPH = TrajHelper.getSpeedFromTraj(target.traj);
          const meanMPH = TrajHelper.getSpeedFromTraj(meanTraj);

          if (Math.abs(targetMPH - meanMPH) > spec.deltaSpeedMPH) {
            reasons.push(GroupRejectedReason.Speed);
          }
          break;
        }

        // asking for breaks => checks both break x and z
        case GroupCriteria.Breaks: {
          const tBreaks = BallHelper.getSafePitchBreaks(target);

          if (!tBreaks) {
            reasons.push(GroupRejectedReason.NoTargetBreaks);
            break;
          }

          const mBreaks = meanObject(
            shots.map((s) => BallHelper.getSafeShotBreaks(s))
          );

          if (!mBreaks) {
            reasons.push(GroupRejectedReason.NoMeanBreaks);
            break;
          }

          if (
            Math.abs(tBreaks.xInches - mBreaks.xInches) > spec.deltaBreaksInches
          ) {
            reasons.push(GroupRejectedReason.BreakX);
          }

          if (
            Math.abs(tBreaks.zInches - mBreaks.zInches) > spec.deltaBreaksInches
          ) {
            reasons.push(GroupRejectedReason.BreakZ);
          }

          break;
        }

        case GroupCriteria.Spins: {
          const bss = compact(shots.map((s) => s.bs));
          const meanBs = meanObject(bss);

          if (!meanBs) {
            reasons.push(GroupRejectedReason.NoMeanBs);
            break;
          }

          if (Math.abs(target.bs.wx - meanBs.wx) > spec.deltaSpinsRPM) {
            reasons.push(GroupRejectedReason.SpinX);
          }

          if (Math.abs(target.bs.wy - meanBs.wy) > spec.deltaSpinsRPM) {
            reasons.push(GroupRejectedReason.SpinY);
          }

          if (Math.abs(target.bs.wz - meanBs.wz) > spec.deltaSpinsRPM) {
            reasons.push(GroupRejectedReason.SpinZ);
          }

          break;
        }

        case GroupCriteria.NewestInZone: {
          const newestShot = maxBy(shots, (m) =>
            parseISO(m._created).getTime()
          );

          if (!newestShot) {
            reasons.push(GroupRejectedReason.NoNewestShot);
            break;
          }

          const inZone = PlateHelper.isPlateInZone({
            plateFt: TrajHelper.getPlateLoc(newestShot.traj),
            szDef: VISIBLE_STRIKEZONE,
            bufferFt: SZ_GREEN_BUFFER_FT,
          });

          if (!inZone) {
            reasons.push(GroupRejectedReason.NewestInZone);
            break;
          }

          break;
        }

        default: {
          break;
        }
      }
    }

    if (reasons.length > 0) {
      console.warn({
        event: 'filterGroup found reasons to reject',
        target: target,
        shots: shots,
        criteria: criteria,
        trainingThreshold: trainingThreshold,
        reasons: reasons,
      });
    }

    return {
      reject: reasons.length > 0,
      // e.g. if multiple filters require meanTraj but it's empty
      reasons: ArrayHelper.unique(reasons),
    };
  }

  static summarizeCollectionShots(
    shots: IRawCollectionShotGroup[],
    includeLowConfidence: boolean
  ): ICollectionShotGroup[] {
    const output: ICollectionShotGroup[] = [];

    shots.forEach((group) => {
      if (!group.pitch) {
        // shouldn't trigger, unless a pitch was deleted
        return;
      }

      const criteria = getConfidenceCriteria(group.pitch.priority);

      const filtered = group.shots.filter((s) => {
        if (includeLowConfidence) {
          return true;
        }

        const { reject } = ShotRejectionHelper.filterShot({
          shot: s,
          criteria: criteria,
        });

        return !reject;
      });

      const total = filtered.length;
      const aiming = filtered.filter((s) => s.aiming).length;

      const o: ICollectionShotGroup = {
        ...group,
        shots: filtered,
        total: total,
        aiming: aiming,
        analysis: total - aiming,
      };

      output.push(o);
    });

    return output;
  }
}
